import React, { useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment-timezone';
import 'react-datepicker/dist/react-datepicker.css';
import { post_api as postApi } from '../../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../../utils/urls';
import ConfirmSessionDetails from '../../sessions/request_session_dashboard/ConfirmSessionDetails';
import './request_session.css';

/**
 * Request a Session — care-team popup (Figma node 7997-18479).
 *
 * Captures a new session request (student date/time, duration, coach gender, language, goals,
 * specific requirements) and, on Next, moves to the confirmation step before broadcasting to
 * matched teachers.
 *
 * On Confirm it POSTs to /v2/session_request/care/create (goserver, ops token) which matches
 * teachers for the future slot and fans the request out to them (first-accept-wins).
 */

const DURATION_OPTIONS = [30, 45, 60, 90];
const DEFAULT_DURATION = 60;

const GENDER_OPTIONS = [
  { value: 'NO_PREFERENCE', label: 'No Preference' },
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' }
];

// Deferred (part of another project) — rendered per design; keep behind go-live wiring on the API.
// Multi-select: top-level languages + an "Indian Languages" group (per PRD).
const LANGUAGE_OPTIONS = ['English', 'Spanish', 'French'];
const INDIAN_LANGUAGE_OPTIONS = ['Hindi', 'Tamil', 'Telugu', 'Kannada', 'Malayalam'];

// Placeholder until goals come from GET /v2/instant_booking/student/preference/options (by funnel type).
const GOAL_OPTIONS = [
  'Build Strength',
  'Increase Flexibility',
  'Boost Energy Levels',
  'Reduce Aches and Pain',
  'Stress Relief',
  'Lose Weight',
  'Manage a Health Condition',
  'Sports Injury',
  'Prenatal or Postnatal'
];

const field_label_style = {
  display: 'block',
  fontSize: 13.5,
  fontWeight: 500,
  marginBottom: 8,
  color: '#264150'
};

const field_base = {
  width: '100%',
  height: 46,
  boxSizing: 'border-box',
  padding: '0 16px',
  borderRadius: 8,
  border: '1px solid var(--input-border)',
  background: 'var(--input-bg)',
  fontSize: 15,
  fontWeight: 500,
  outline: 'none',
  appearance: 'none',
  WebkitAppearance: 'none',
  cursor: 'pointer'
};

const primary_btn = {
  minWidth: 120,
  padding: '14px 22px',
  borderRadius: 14,
  border: 'none',
  background: 'var(--brand-primary)',
  color: 'var(--text-inverse)',
  fontSize: 17,
  fontWeight: 600,
  cursor: 'pointer',
  boxShadow: '0px 2px 4px 0px rgba(248, 102, 36, 0.15)'
};

const chevron = (
  <span
    aria-hidden
    style={{
      position: 'absolute',
      right: 16,
      top: '50%',
      transform: 'translateY(-25%) rotate(45deg)',
      width: 8,
      height: 8,
      borderRight: '2px solid var(--input-placeholder)',
      borderBottom: '2px solid var(--input-placeholder)',
      pointerEvents: 'none'
    }}
  />
);

function Field({ label, children }) {
  return (
    <div>
      <span style={field_label_style}>{label}</span>
      <div style={{ position: 'relative' }}>{children}</div>
    </div>
  );
}

const SLOT_INTERVAL_MIN = 30;
const SLOTS_PER_DAY = (24 * 60) / SLOT_INTERVAL_MIN;

function TimeSlot({ label, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: 43,
        borderRadius: 7,
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        background: selected ? 'var(--brand-primary-subtle-bg)' : 'var(--input-bg)',
        border: `1px solid ${selected ? 'var(--brand-primary-subtle-border)' : 'var(--input-bg)'}`,
        color: selected ? 'var(--brand-primary)' : 'var(--input-text)'
      }}
    >
      {label}
    </button>
  );
}

function GoalRow({ label, checked, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '9px 4px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        textAlign: 'left'
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          flexShrink: 0,
          borderRadius: 6,
          background: checked ? 'var(--brand-primary)' : 'var(--surface-sunken)',
          border: `1px solid ${checked ? 'var(--brand-primary)' : 'var(--border-default)'}`,
          color: 'var(--text-inverse)',
          fontSize: 12,
          lineHeight: 1
        }}
      >
        {checked ? '✓' : ''}
      </span>
      <span
        style={{
          fontSize: 14,
          fontWeight: 500,
          color: checked ? 'var(--brand-primary)' : 'var(--text-primary)'
        }}
      >
        {label}
      </span>
    </button>
  );
}

function RequestSession({
  details = {},
  student_uuid,
  iana_timezone,
  goal_options,
  initialValues = {},
  initialStep = 'form',
  close,
  set_notification_variable
}) {
  // Goals shown = the SAME "1-on-1 fitness funnel" goals teachers mark on their profile
  // (config_values.goals_for_1on1_fitness), so care selections map 1:1 to teacher goals for
  // matching. Fall back to the static list only if the config hasn't loaded.
  const goalList = Array.isArray(goal_options) && goal_options.length ? goal_options : GOAL_OPTIONS;
  // initialValues prefills the form when reused for the dashboard "Resend" flow (Edit → this form,
  // prefilled from the existing request); empty for the create flow from the student profile.
  const [step, setStep] = useState(initialStep); // 'form' | 'confirm'
  const [submitting, setSubmitting] = useState(false);
  const [sessionDate, setSessionDate] = useState(initialValues.sessionDate || null);
  const [dateOpen, setDateOpen] = useState(false);
  const [pickerStep, setPickerStep] = useState('date'); // 'date' | 'time'
  const [pickedDate, setPickedDate] = useState(null);
  const dateRef = useRef(null);
  const [duration, setDuration] = useState(initialValues.duration || DEFAULT_DURATION);
  const [coachGender, setCoachGender] = useState(initialValues.coachGender || 'NO_PREFERENCE');
  const [languages, setLanguages] = useState(initialValues.languages || []);
  const [languagesOpen, setLanguagesOpen] = useState(false);
  const languagesRef = useRef(null);
  const [goals, setGoals] = useState(initialValues.goals || []);
  const [goalsOpen, setGoalsOpen] = useState(false);
  const [specificNeeds, setSpecificNeeds] = useState(initialValues.specificNeeds || '');
  const goalsRef = useRef(null);

  // Render times in the CMS-selected timezone (the header "Timezone" dropdown), matching the
  // rest of the student profile, then fall back to the student's own zone, then the browser.
  // Previously this defaulted to moment.tz.guess() (the agent's browser), so every student
  // wrongly showed the agent's local zone (e.g. BST) regardless of the dropdown selection.
  const studentZone = iana_timezone || details.iana_timezone || moment.tz.guess();
  const zoneAbbr = moment.tz(studentZone).zoneAbbr();

  useEffect(() => {
    if (!goalsOpen) return undefined;
    const on_click_outside = (e) => {
      if (goalsRef.current && !goalsRef.current.contains(e.target)) setGoalsOpen(false);
    };
    document.addEventListener('mousedown', on_click_outside);
    return () => document.removeEventListener('mousedown', on_click_outside);
  }, [goalsOpen]);

  useEffect(() => {
    if (!languagesOpen) return undefined;
    const on_click_outside = (e) => {
      if (languagesRef.current && !languagesRef.current.contains(e.target)) setLanguagesOpen(false);
    };
    document.addEventListener('mousedown', on_click_outside);
    return () => document.removeEventListener('mousedown', on_click_outside);
  }, [languagesOpen]);

  useEffect(() => {
    if (!dateOpen) return undefined;
    const on_click_outside = (e) => {
      if (dateRef.current && !dateRef.current.contains(e.target)) setDateOpen(false);
    };
    document.addEventListener('mousedown', on_click_outside);
    return () => document.removeEventListener('mousedown', on_click_outside);
  }, [dateOpen]);

  // 30-min start-time slots for the picked date, in the student's local wall clock (past slots
  // hidden when the picked date is today). Real availability will come from the API later.
  const time_slots = useMemo(() => {
    if (!pickedDate) return [];
    const startOfDay = moment(pickedDate).startOf('day');
    const now = moment();
    const isToday = startOfDay.isSame(now, 'day');
    const slots = [];
    for (let i = 0; i < SLOTS_PER_DAY; i += 1) {
      const t = startOfDay.clone().add(i * SLOT_INTERVAL_MIN, 'minutes');
      if (isToday && t.isBefore(now)) continue;
      slots.push({ date: t.toDate(), label: `${t.format('h:mm A')} ${zoneAbbr}` });
    }
    return slots;
  }, [pickedDate, zoneAbbr]);

  const open_date_picker = () => {
    setPickerStep(sessionDate ? 'time' : 'date');
    if (sessionDate && !pickedDate) setPickedDate(sessionDate);
    setDateOpen(true);
  };

  const on_calendar_change = (d) => {
    setPickedDate(d);
    setPickerStep('time');
  };

  const on_slot_pick = (slotDate) => {
    setSessionDate(slotDate);
    setDateOpen(false);
  };

  // Interpret the picked wall-clock time in the student's timezone, then show the IST equivalent (PRD).
  const istTime = useMemo(() => {
    if (!sessionDate) return '';
    const m = moment.tz(moment(sessionDate).format('YYYY-MM-DDTHH:mm'), studentZone);
    if (!m.isValid()) return '';
    return m.clone().tz('Asia/Kolkata').format('ddd, MMM D, h:mm A [IST]');
  }, [sessionDate, studentZone]);

  const toggle_goal = (goal) =>
    setGoals((prev) => (prev.includes(goal) ? prev.filter((g) => g !== goal) : [...prev, goal]));

  const toggle_language = (l) =>
    setLanguages((prev) => (prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l]));

  const go_to_confirm = () => {
    if (!sessionDate) {
      set_notification_variable?.(true, 'error', 'Please select a date and time for the session.');
      return;
    }
    if (!coachGender) {
      set_notification_variable?.(true, 'error', 'Please select the gender of coach.');
      return;
    }
    if (!goals.length) {
      set_notification_variable?.(true, 'error', 'Please select at least one goal.');
      return;
    }
    setStep('confirm');
  };

  const on_confirm = () => {
    const uuid = student_uuid || details.uuid || details.student_uuid;
    if (!uuid) {
      set_notification_variable?.(
        true,
        'error',
        'Missing student. Please reopen from the profile.'
      );
      return;
    }
    if (submitting) return;
    const trimmed_needs = specificNeeds.trim();
    const payload = {
      student_uuid: uuid,
      start_time: moment.tz(moment(sessionDate).format('YYYY-MM-DDTHH:mm'), studentZone).valueOf(),
      duration: Number(duration),
      coach_gender: coachGender,
      language: languages.length ? languages.join(', ') : null,
      goals,
      specific_needs: trimmed_needs || null
    };
    setSubmitting(true);
    const url = getApiUrl('/v2/session_request/care/create', false, true);
    postApi(url, payload, true)
      .then((resp) => {
        const data = resp.data?.data || {};
        const count = data.teachers_count || 0;
        set_notification_variable?.(
          true,
          'success',
          `Session request sent to ${count} matched teacher${count === 1 ? '' : 's'}.`
        );
        close?.();
      })
      .catch((e) => {
        setSubmitting(false);
        let msg;
        try {
          msg = e.response.data.message || e.response.data.reason;
        } catch (err) {
          msg = 'Could not create the session request. Please try again.';
        }
        set_notification_variable?.(true, 'error', msg);
      });
  };

  const genderLabel = GENDER_OPTIONS.find((g) => g.value === coachGender)?.label;

  if (step === 'confirm') {
    const confirm_row = {
      student_time: `${moment(sessionDate).format('MMM D, YYYY')} - ${moment(sessionDate).format(
        'h:mm A'
      )} ${zoneAbbr}`,
      teacher_time: istTime,
      duration: `${duration} Mins`,
      language: languages.length ? languages.join(', ') : '-',
      gender: genderLabel,
      goals,
      comment: specificNeeds || '-'
    };
    return (
      <ConfirmSessionDetails
        row={confirm_row}
        confirming={submitting}
        onEdit={() => setStep('form')}
        onConfirm={on_confirm}
      />
    );
  }

  return (
    <div>
      <p style={{ fontSize: 19, lineHeight: '22px', fontWeight: 600, margin: 0, color: '#333' }}>
        Request a Session
      </p>
      <p
        style={{
          fontSize: 14.5,
          fontWeight: 500,
          margin: '7px 0 20px',
          color: '#939393'
        }}
      >
        Select the options below to create a new session request
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '22px 30px' }}>
        <div ref={dateRef}>
          <span style={field_label_style}>
            {`Student Date & Time${zoneAbbr ? ` (${zoneAbbr})` : ''}`}
          </span>
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => (dateOpen ? setDateOpen(false) : open_date_picker())}
              style={{
                ...field_base,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                textAlign: 'left',
                color: sessionDate ? 'var(--input-text)' : 'var(--input-placeholder)',
                borderColor: dateOpen ? 'var(--input-focus-border)' : 'var(--input-border)',
                margin: '0'
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {sessionDate
                  ? `${moment(sessionDate).format('MMM D, YYYY · h:mm A')} ${zoneAbbr}`
                  : 'Choose date & time'}
              </span>
              <span
                aria-hidden
                style={{
                  width: 8,
                  height: 8,
                  marginLeft: 8,
                  flexShrink: 0,
                  transform: 'translateY(-2px) rotate(45deg)',
                  borderRight: '2px solid var(--input-placeholder)',
                  borderBottom: '2px solid var(--input-placeholder)'
                }}
              />
            </button>

            {dateOpen ? (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  left: 0,
                  zIndex: 30,
                  width: pickerStep === 'time' ? 364 : 'auto',
                  maxWidth: '86vw',
                  padding: '18px 20px 16px',
                  borderRadius: 12,
                  background: 'var(--surface-card)',
                  border: '1px solid var(--border-default)',
                  boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.12)'
                }}
              >
                {pickerStep === 'date' ? (
                  <DatePicker
                    inline
                    selected={pickedDate || sessionDate}
                    onChange={on_calendar_change}
                    minDate={new Date()}
                    calendarClassName="rs-datepicker"
                  />
                ) : (
                  <>
                    <p
                      style={{
                        margin: '0 0 14px',
                        fontSize: 17,
                        fontWeight: 700,
                        color: 'var(--text-primary)'
                      }}
                    >
                      Select your start time
                    </p>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 13,
                        maxHeight: 268,
                        overflowY: 'auto',
                        paddingRight: 4
                      }}
                    >
                      {time_slots.map((slot) => (
                        <TimeSlot
                          key={slot.label}
                          label={slot.label}
                          selected={
                            !!sessionDate && moment(sessionDate).isSame(slot.date, 'minute')
                          }
                          onClick={() => on_slot_pick(slot.date)}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => setPickerStep('date')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        marginTop: 14,
                        padding: '4px 0',
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--text-muted)',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: 6,
                          height: 6,
                          transform: 'rotate(135deg)',
                          borderRight: '2px solid var(--text-muted)',
                          borderBottom: '2px solid var(--text-muted)'
                        }}
                      />
                      Back
                    </button>
                  </>
                )}
              </div>
            ) : null}
          </div>
          {istTime ? (
            <p style={{ margin: '10px 0 0', fontSize: 12.5, color: '#7B8286' }}>
              Indian time {istTime}
            </p>
          ) : null}
        </div>

        <Field label="Duration">
          <select
            style={{ ...field_base, color: 'var(--input-text)' }}
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
          >
            {DURATION_OPTIONS.map((d) => (
              <option key={d} value={d}>
                {d} Mins Duration
              </option>
            ))}
          </select>
          {chevron}
        </Field>

        <Field label="Gender of coach">
          <select
            style={{ ...field_base, color: 'var(--input-text)' }}
            value={coachGender}
            onChange={(e) => setCoachGender(e.target.value)}
          >
            {GENDER_OPTIONS.map((g) => (
              <option key={g.value} value={g.value}>
                {g.label}
              </option>
            ))}
          </select>
          {chevron}
        </Field>

        <div ref={languagesRef}>
          <span style={field_label_style}>
            Language spoken{' '}
            <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span>
          </span>
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setLanguagesOpen((o) => !o)}
              style={{
                ...field_base,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                textAlign: 'left',
                color: languages.length ? 'var(--input-text)' : 'var(--input-placeholder)',
                borderColor: languagesOpen ? 'var(--input-focus-border)' : 'var(--input-border)'
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {languages.length ? languages.join(', ') : 'Select Language'}
              </span>
              <span
                aria-hidden
                style={{
                  width: 8,
                  height: 8,
                  marginLeft: 8,
                  flexShrink: 0,
                  transform: languagesOpen ? 'translateY(3px) rotate(-135deg)' : 'rotate(45deg)',
                  borderRight: '2px solid var(--input-placeholder)',
                  borderBottom: '2px solid var(--input-placeholder)'
                }}
              />
            </button>
            {languagesOpen ? (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  left: 0,
                  right: 0,
                  zIndex: 30,
                  maxHeight: 320,
                  overflowY: 'auto',
                  padding: '14px 16px',
                  borderRadius: 12,
                  background: 'var(--surface-card)',
                  border: '1px solid var(--border-default)',
                  boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.12)'
                }}
              >
                {LANGUAGE_OPTIONS.map((l) => (
                  <GoalRow
                    key={l}
                    label={l}
                    checked={languages.includes(l)}
                    onToggle={() => toggle_language(l)}
                  />
                ))}
                <p
                  style={{
                    margin: '10px 0 2px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--text-muted)'
                  }}
                >
                  Indian Languages
                </p>
                {INDIAN_LANGUAGE_OPTIONS.map((l) => (
                  <GoalRow
                    key={l}
                    label={l}
                    checked={languages.includes(l)}
                    onToggle={() => toggle_language(l)}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ gridColumn: '1 / -1' }} ref={goalsRef}>
          <span style={field_label_style}>Any goals teacher should know</span>
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setGoalsOpen((o) => !o)}
              style={{
                ...field_base,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                textAlign: 'left',
                color: 'var(--input-placeholder)',
                borderColor: goalsOpen ? 'var(--input-focus-border)' : 'var(--input-border)',
                margin: '0'
              }}
            >
              <span>Select goals</span>
              <span
                aria-hidden
                style={{
                  width: 8,
                  height: 8,
                  marginRight: 4,
                  transform: goalsOpen ? 'translateY(3px) rotate(-135deg)' : 'rotate(45deg)',
                  borderRight: '2px solid var(--input-placeholder)',
                  borderBottom: '2px solid var(--input-placeholder)'
                }}
              />
            </button>
            {goalsOpen ? (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  left: 0,
                  right: 0,
                  zIndex: 30,
                  maxHeight: 320,
                  overflowY: 'auto',
                  padding: '14px 16px',
                  borderRadius: 12,
                  background: 'var(--surface-card)',
                  border: '1px solid var(--border-default)',
                  boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.12)'
                }}
              >
                <p
                  style={{
                    margin: '0 0 6px',
                    fontSize: 15,
                    fontWeight: 600,
                    color: 'var(--text-primary)'
                  }}
                >
                  Select goals
                </p>
                {goalList.map((goal) => (
                  <GoalRow
                    key={goal}
                    label={goal}
                    checked={goals.includes(goal)}
                    onToggle={() => toggle_goal(goal)}
                  />
                ))}
              </div>
            ) : null}
          </div>
          {goals.length && !goalsOpen ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 14 }}>
              {goals.map((goal) => (
                <span
                  key={goal}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 14px',
                    borderRadius: 10,
                    fontSize: 13,
                    fontWeight: 500,
                    background: 'var(--brand-primary-subtle-bg)',
                    border: '1px solid var(--brand-primary-subtle-border)',
                    color: 'var(--brand-primary-subtle-text)'
                  }}
                >
                  {goal}
                  <button
                    type="button"
                    aria-label={`Remove ${goal}`}
                    onClick={() => toggle_goal(goal)}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--brand-primary-subtle-text)',
                      cursor: 'pointer',
                      fontSize: 13,
                      lineHeight: 1,
                      padding: 0
                    }}
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <span style={field_label_style}>
            Student’s specific requirements for today’s session?
          </span>
          <textarea
            style={{
              width: '100%',
              height: 110,
              minHeight: 110,
              boxSizing: 'border-box',
              padding: '14px 18px',
              borderRadius: 7,
              border: '1px solid var(--input-bg)',
              background: 'var(--input-bg)',
              color: 'var(--input-text)',
              fontSize: 15,
              fontWeight: 500,
              resize: 'vertical',
              outline: 'none'
            }}
            value={specificNeeds}
            onChange={(e) => setSpecificNeeds(e.target.value)}
            placeholder="Type here... (Optional)"
          />
        </div>
      </div>

      <div style={{ marginTop: 26 }}>
        <button type="button" style={primary_btn} onClick={go_to_confirm}>
          Next
        </button>
      </div>
    </div>
  );
}

export default RequestSession;
